// Copied from flobi-flow src/lib/composer/modelInputs.js (518142c9, 13 Oct); keep in step with Flow.
/**
 * The model's own input schema (ai.models.list → modelInputs), flattened:
 * one entry per input reference, with the levels it shows at (SECOND = L2,
 * THIRD = L3; HIDDEN entries dropped), its type, constraints and group.
 * Pure — moved here from the image Composer's kit (which re-exports it) so
 * the run checks (./runChecks) read a model's inputs without React.
 */

/**
 * The catalogue shares ONE input row per reference across node types, so image
 * models carry copy written for video / outpaint ("Video aspect ratio.", "Describe
 * what should fill the extended areas.", "std: 720p. pro: 1080p."). Found
 * 2026-09-22 — it's catalogue DATA (admin-edited, not in any repo). The image
 * node shows its own copy for its core inputs and drops any other description
 * that plainly belongs to another node type.
 */
const IMAGE_COPY = {
  prompt: "Describe the image you want.",
  images_urls: "Images the model uses as reference.",
  aspectRatio: "The image's shape — width to height.",
};
const FOREIGN_COPY = /\bvideo\b|first frame|\bframes?\b|\b(720|1080)p\b|extended areas|expanded image|transparent canvas/i;
const imageDescription = (ref, text) => IMAGE_COPY[ref] ?? (FOREIGN_COPY.test(text || "") ? "" : text || "");

export function parseInputs(modelInputs, { hidden = false } = {}) {
  const out = [];
  for (const [ref, entries] of Object.entries(modelInputs || {})) {
    // `hidden`: only the inputs the canvas prompt bar shows (level HIDDEN =
    // "in the bar, not on the node") instead of the node's own.
    const shown = (entries || []).filter((e) => (hidden ? e.level === "HIDDEN" : e.level && e.level !== "HIDDEN"));
    if (!shown.length) continue;
    const e = shown.find((x) => x.level === "THIRD") || shown[0];
    const d = e.modelInputData || {};
    out.push({
      ref,
      name: e.name || ref,
      type: e.InputType?.name, // TEXT_AREA | TEXT | IMAGE | LIST_VERTICAL | BOOL | NUMBER
      dataType: e.InputType?.type, // TEXT | IMAGE | BOOLEAN | INTEGER
      levels: new Set(shown.map((x) => x.level)),
      required: !!d.isRequired,
      values: d.values || [],
      def: d.defaultValue,
      min: d.minValue,
      max: d.maxValue,
      // A model on a Fabric machine says its own step (temperature 0..2 by 0.05, top-p 0..1 by 0.01); without it the
      // slider's own fallback snapped them to 0 / 0.5 / 1. Cloud rows send none, so theirs are as before.
      step: d.step ?? null,
      limit: d.nbInputs || d.mediaLimit || null,
      charLimit: d.characterLimit || null,
      group: e.InputGroup?.label || null,
      placement: e.placement,
      description: imageDescription(ref, e.description),
      order: (e.InputGroup?.position ?? 0) * 100 + (e.position ?? 0),
      raw: e, // the catalogue entry (the classic input menu / create-input-node need it)
    });
  }
  return out.sort((a, b) => a.order - b.order);
}

/**
 * The values a list setting offers (`ref`: "aspectRatio", "quality"…), as the
 * node's rows list them — its SECOND / THIRD entry, else the HIDDEN one the
 * prompt bar reads. One list for the node, the bar and the multi-select
 * toolbar (Farah, 29 Sep: the toolbar keeps its look, shows the node's
 * options and labels). → [] when the model doesn't take it.
 */
export function settingValues(modelRaw, ref) {
  const mi = modelRaw?.modelInputs;
  const inp = parseInputs(mi).find((i) => i.ref === ref) || parseInputs(mi, { hidden: true }).find((i) => i.ref === ref);
  return Array.isArray(inp?.values) ? inp.values.map(String).filter((v) => v.trim()) : [];
}
